/* himedi-summary-edit.js — "edit your checkup" on /pages/checkup-summary (2026-09-25).
 *
 * Phones and anything under 1080px: one closed row above the calendar ("Your checkup · Baseline +
 * Cancer + 1 add-on · $923 · Edit"). It is a real button (aria-expanded) and opens the rows in place.
 * 1080px and up: the same rows in a card to the right of the calendar, always open. The calendar
 * column stays 724px wide (the booking app clips its weekday labels below that).
 *
 * WHERE THE TRUTH LIVES. Nothing here keeps its own copy of the build. Every read goes through
 * window.HimediFlow (himedi-checkup-flow.js): state(), total(), isCovered(), upgradeAvailable(),
 * catalog(). Every edit mutates HimediFlow.state() and hands it back through setState(), which is the
 * same object the flow file's booking interceptor and reconcile read. That matters: the booking app's
 * add-to-cart is rewritten to the Checkup variant for the build IN THAT MEMORY (comboVariant), so an
 * edit that only touched localStorage would book the old checkup.
 *
 * NO BASE SWAP (Gabe, 2026-09-25): the base row shows its name and price only. Changing between the
 * Baseline and the Whole Body MRI is done on the builder.
 *
 * WHAT RELOADS. A focus-area change on the Whole Body MRI (the Baseline checkup it can add) saves and
 * reloads: the section picks the MRI variant from the stored build at page load and re-asserts it every
 * 400ms, so an in-place change would book the variant the page loaded with. On the Baseline every
 * combination books on the same calendar and the Checkup variant is picked from the flow's state at
 * add-to-cart, so focus areas and add-ons change in place.
 *
 * FOCUS AREAS behave exactly as on the builder (Gabe, 2026-09-25): adding one sets upgrades[id]; an
 * add-on it covers stays chosen in the saved build and is simply not charged while covered. Removing it
 * clears upgrades[id] only, so an add-on the person chose comes back and is charged, and an add-on they
 * never chose never appears.
 *
 * NOTHING HERE KNOWS ABOUT A PROMOTION. Discount amounts and names on booked checkups come from the
 * cart (final_line_price, discount_application.title).
 *
 * PRICES. Booked checkups show what /cart.js charges (final_line_price, so an automatic discount such
 * as a code or automatic discount is in it). The build not booked yet shows the same pre-discount total as
 * the builder.
 *
 * BOOKED CHECKUPS (2026-09-25, Gabe: edit on this page any time, never sent to the cart). Every checkup
 * with a time held (one dated booking line in its own group) can, right here: take an add-on off, add
 * one (the add-on sheet for that checkup's base, with the focus-area cover rule applied to ITS focus
 * areas), and take a focus area off. Adding a focus area or changing the base after booking stays out.
 *   - Add-ons are their own lines, so the dated line is never written. An add is one /cart/add.js line
 *     carrying the group's tags exactly as the flow's own reconcile builds them (groupFields + the four
 *     keys); a remove is the cart page's own call (/cart/update.js, that one key set to 0).
 *   - A focus area lives in the dated line's variant, which /cart/change.js cannot change. So: add the
 *     new variant (same combination table, same Standard/Promo twin the booking already got) carrying a
 *     byte-for-byte copy of the dated line's properties, check it in /cart.js, and only then remove the
 *     old line. Any failed step puts the cart back exactly as it was. A covered add-on the person chose
 *     on this build comes back when its focus area goes (the builder's rule); nothing unchosen is added.
 *   - Every write re-reads the cart first and last: only the intended line may appear or disappear.
 *   - The saved build is kept in step only when it describes that group (the pinned group, nothing
 *     missing or extra): removals before the write, additions after it, so the flow's reconcile, which
 *     only adds, never puts a removed test back or doubles an added one. A sealed group stays sealed:
 *     the seal stops the flow silently reconciling a changed build into a handed-off checkout; an edit
 *     made here is explicit and targets that one group, and its build fingerprint moves with it.
 *
 * THE ADD-ON SHEET is the add-on page's own markup (snippets/himedi-addon-groups.liquid, rendered into
 * <template id="ecAddonGroups">) wired by the add-on page's own function, HimediFlow.wireAddons() — so
 * the whole-row tap, the "already included in your Cancer focus area" lock and the deselect of a
 * covered add-on are the add-on page's behaviour exactly, not a copy of it.
 *
 * If HimediFlow is missing those members (an older flow file), this does nothing and the page is
 * exactly as it was.
 */
(function () {
  'use strict';
  var F = window.HimediFlow;
  if (!F || typeof F.catalog !== 'function' || typeof F.wireAddons !== 'function' || typeof F.setState !== 'function') return;
  // booking-app mode only for the card; the booked-checkup edits below are also exported for the cart page (see the end)

  var desk = window.matchMedia('(min-width: 1080px)');
  var card, bookingSec, bookingH3, bookingH3Orig, bookingSub, bookingSubOrig, commitBox;
  var HELD_ERR = null, HELD_BUSY = false, HOPEN = {}, HSHEET = null;
  var OPEN = false, CART = null, sheetKind = null, sheetFrom = null, focusDraft = null, OFFER = null, OFFER_ASKED = false;

  /* ---------- reads, all through the flow file ---------- */
  function C() { return F.catalog(); }
  function S() { return F.state(); }
  function edit(fn) { var s = F.state(); fn(s); F.setState(s); }
  function money(n) { n = Number(n || 0); var f = Math.abs(n - Math.round(n)) < 0.005 ? 0 : 2; return (n < 0 ? '−$' : '$') + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: f, maximumFractionDigits: f }); }
  function esc(t) { return String(t == null ? '' : t).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function nb(t) { return esc(t).replace(/\(([^)]*)\)/g, function (m) { return m.replace(/ /g, ' '); }); }
  function shortBase(name) { return name.replace(/ checkup$/i, ''); }
  function upName(k) { var u = C().UPGRADES[k]; return k === 'checkup' ? 'Baseline checkup' : u.name + ' focus area'; }
  function onWb() { return F.baseKey() === 'wbmri'; }

  function items() {
    var c = C(), s = S(), bk = F.baseKey(), b = c.BASES[bk], out = [{ kind: 'base', id: bk, name: b.name, price: b.price }];
    Object.keys(c.UPGRADES).forEach(function (k) {
      if (s.upgrades[k] && F.upgradeAvailable(k)) out.push({ kind: 'up', id: k, name: upName(k), price: c.UPGRADES[k].price });
    });
    Object.keys(c.ADDONS).forEach(function (k) {
      var a = c.ADDONS[k];
      if (s.addons[k] && a.price && !F.isCovered(k)) out.push({ kind: 'addon', id: k, name: a.name, price: a.price });
    });
    return out;
  }
  // add-ons the customer picked that a chosen focus area now contains (not charged; the builder's rule)
  function coveredPicked() {
    var c = C(), s = S();
    return Object.keys(c.ADDONS).filter(function (k) { return s.addons[k] && F.isCovered(k); }).map(function (k) {
      return { id: k, name: c.ADDONS[k].name, by: c.UPGRADES[c.ADDONS[k].includedIn].name };
    });
  }
  function summary() {
    var l = items(), parts = [shortBase(C().BASES[F.baseKey()].name)];
    l.forEach(function (i) { if (i.kind === 'up') parts.push(i.id === 'checkup' ? 'Baseline' : C().UPGRADES[i.id].name); });
    var n = l.filter(function (i) { return i.kind === 'addon'; }).length;
    if (n) parts.push(n + '\u00a0add\u2011on' + (n > 1 ? 's' : '')); // no break inside "2 add-ons"
    return parts.join(' + ');
  }
  function focusOptions() { // focus areas this base offers that are not on the build yet
    var c = C(), s = S();
    return Object.keys(c.UPGRADES).filter(function (k) { return F.upgradeAvailable(k) && !s.upgrades[k]; });
  }

  /* ---------- the cart: earlier checkups, and whether this one is booked ---------- */
  function readCart() {
    return fetch('/cart.js?_=' + Date.now(), { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { CART = c; return c; });
  }
  function shortDate(d) {
    if (!d) return '';
    var m = /([A-Z][a-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?,?\s*(\d{4})/.exec(d), dt = m ? new Date(m[1] + ' ' + m[2] + ', ' + m[3]) : null;
    if (!dt || isNaN(dt)) return d;
    return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }
  /* A cart line -> the rows the editable card would show for it, named from the flow's catalog by
     variant id (never from the product title). A Checkup combo line becomes "Baseline checkup" plus one
     row per focus area, priced from the catalog, but only when those prices add up to what the line was
     sold at; otherwise it stays one row at the line's price. Unknown variants keep their product title. */
  var FOCUS_ORDER = ['heart', 'brain', 'cancer'];
  /* 2026-10-14: a held Whole Body MRI line can be the Promo twin of the Standard MRI (or MRI + Checkup);
     read it as its Standard so it is named, priced and edited like the MRI it is. */
  function stdOf(v) { var m = C().WBMRI_PROMO_TO_STD || {}; v = Number(v); return m[String(v)] ? Number(m[String(v)]) : v; }
  function lineRows(l) {
    var c = C(), v = stdOf(l.variant_id), q = l.quantity || 1, orig = (l.original_line_price != null ? l.original_line_price : l.line_price) / 100;
    var parts = null, k, b;
    for (k in c.COMBO_VARIANTS) {
      var e = c.COMBO_VARIANTS[k];
      if (Number(e.std) === v || Number(e.sep) === v) {
        parts = [{ name: c.BASES.baseline.name, price: c.BASES.baseline.price }];
        FOCUS_ORDER.forEach(function (f) { if (k.split('+').indexOf(f) !== -1) parts.push({ name: upName(f), price: c.UPGRADES[f].price, focus: f }); });
      }
    }
    if (!parts && v === Number(c.WBMRI_COMBO_VARIANT)) parts = [{ name: c.BASES.wbmri.name, price: c.BASES.wbmri.price }, { name: upName('checkup'), price: c.UPGRADES.checkup.price, focus: 'checkup' }];
    if (!parts) for (b in c.BASES) if (Number(c.BASES[b].variant) === v) parts = [{ name: c.BASES[b].name, price: c.BASES[b].price }];
    if (!parts) for (k in c.UPGRADES) if (Number(c.UPGRADES[k].variant) === v) parts = [{ name: upName(k), price: c.UPGRADES[k].price }];
    if (!parts) for (b in c.ADDONS_BY_BASE) for (k in c.ADDONS_BY_BASE[b]) if (!parts && Number(c.ADDONS_BY_BASE[b][k].variant) === v) parts = [{ name: c.ADDONS_BY_BASE[b][k].name, price: c.ADDONS_BY_BASE[b][k].price }];
    if (!parts) parts = [{ name: l.product_title, price: orig / q }];
    var sum = parts.reduce(function (a, p) { return a + p.price; }, 0) * q;
    if (Math.abs(sum - orig) > 0.005) parts = [{ name: parts.map(function (p) { return p.name; }).join(' + '), price: orig / q }];
    return parts.map(function (p) { return { name: p.name + (q > 1 ? ' ×' + q : ''), price: p.price * q, focus: p.focus || null, line: l }; });
  }
  /* ---------- a booked checkup, read off its cart lines ---------- */
  var OURS = ['Checkup', '_cgid', '_cseq', '_cv'];
  function comboOf(v) {
    var c = C(), k; v = Number(v);
    for (k in c.COMBO_VARIANTS) { if (Number(c.COMBO_VARIANTS[k].std) === v) return { key: k, twin: 'std' }; if (Number(c.COMBO_VARIANTS[k].sep) === v) return { key: k, twin: 'sep' }; }
    return null;
  }
  // an add-on line of this base: quantity 1, not a checkup, carrying our four group tags and nothing else
  function addonKeyFor(l, base) {
    if (!l || !l.key || (l.quantity || 1) !== 1 || F.isBaseVariant(l.variant_id) || F.lineDate(l)) return null;
    var p = l.properties || {}, k;
    for (k in p) if (Object.prototype.hasOwnProperty.call(p, k) && OURS.indexOf(k) === -1) return null;
    var list = C().ADDONS_BY_BASE[base] || {};
    for (k in list) if (Number(list[k].variant) === Number(l.variant_id)) return k;
    return null;
  }
  /* ok only when the group is exactly what the flow builds: one dated line (a Checkup combination, the
     Whole Body MRI, or the MRI + Baseline combo), one appointment date, and add-on lines of that base. */
  function bookedInfo(cart, grp) {
    var c = C(), info = { gid: grp.gid, ok: false, dated: null, base: null, foci: [], twin: null, addons: {} };
    var dated = grp.lines.filter(function (l) { return F.isDatedBookingLine(l); });
    if (dated.length !== 1 || F.groupDates(cart, grp.gid).length !== 1) return info;
    var dl = info.dated = dated[0], v = stdOf(dl.variant_id), cb = comboOf(v);
    if ((dl.quantity || 1) !== 1) return info;
    if (cb) { info.base = 'baseline'; info.foci = cb.key === 'base' ? [] : cb.key.split('+'); info.twin = cb.twin; }
    else if (v === Number(c.WBMRI_COMBO_VARIANT)) { info.base = 'wbmri'; info.foci = ['checkup']; info.twin = v === Number(dl.variant_id) ? 'std' : 'promo'; }
    else if (v === Number(c.BASES.wbmri.variant)) { info.base = 'wbmri'; info.twin = v === Number(dl.variant_id) ? 'std' : 'promo'; }
    else return info;
    var bad = false;
    grp.lines.forEach(function (l) { if (l === dl) return; var k = addonKeyFor(l, info.base); if (!k || info.addons[k]) bad = true; else info.addons[k] = l; });
    info.ok = !bad;
    return info;
  }
  function coveredIn(info, k) { var a = (C().ADDONS_BY_BASE[info.base] || {})[k]; return !!(a && a.includedIn && info.foci.indexOf(a.includedIn) !== -1); }
  function findGroup(cart, gid) { var g = null; F.canonicalGroups(cart).forEach(function (x) { if (x.gid === gid) g = x; }); return g; }

  function groupView(cart, grp, i) {
    var dates = F.groupDates(cart, grp.gid), p = (grp.lines[0] && grp.lines[0].properties) || {}, rows = [], orig = 0, fin = 0, wb = false, titles = [];
    grp.lines.slice().sort(function (a, b) { return F.isBaseVariant(b.variant_id) - F.isBaseVariant(a.variant_id); }).forEach(function (l) { // the checkup first, then add-ons
      rows = rows.concat(lineRows(l));
      orig += (l.original_line_price != null ? l.original_line_price : l.line_price) || 0;
      fin += (l.final_line_price != null ? l.final_line_price : l.line_price) || 0;
      (l.line_level_discount_allocations || []).forEach(function (d) { var t = d.amount > 0 && d.discount_application && d.discount_application.title; if (t && titles.indexOf(t) === -1) titles.push(t); });
      if (stdOf(l.variant_id) === Number(C().BASES.wbmri.variant) || stdOf(l.variant_id) === Number(C().WBMRI_COMBO_VARIANT)) wb = true;
    });
    var disc = (orig - fin) / 100;
    var info = bookedInfo(cart, grp);
    rows.forEach(function (r) { // what may be taken off this booked checkup
      r.rm = null;
      if (!info.ok) return;
      if (r.focus && r.line === info.dated && (info.base === 'wbmri' ? r.focus === 'checkup' : info.foci.indexOf(r.focus) !== -1)) r.rm = 'focus:' + r.focus;
      else if (!r.focus) { var k = addonKeyFor(r.line, info.base); if (k && info.addons[k] === r.line) r.rm = 'addon:' + k; }
    });
    return { gid: grp.gid, label: p.Checkup || ('Checkup ' + (i + 1)), date: shortDate(dates[0]), series: wb ? 'wbmri' : 'baseline',
      rows: rows, discount: disc > 0.004 ? disc : 0, discountName: titles.join(' + ') || 'Discount', total: fin / 100, editable: info.ok };
  }
  // cart-level discounts (none measured on 2026-09-25, but /cart.js can carry them): shown as their own row
  function cartLevelDiscount(cart) {
    var t = 0; (cart.cart_level_discount_applications || []).forEach(function (a) { t += Number(a.total_allocated_amount) || 0; });
    return t / 100;
  }
  function cartLevelName(cart) { return (cart.cart_level_discount_applications || []).map(function (a) { return a.title; }).filter(Boolean).join(' + ') || 'Order discount'; }
  function model() {
    var cart = CART || { items: [] }, groups = F.canonicalGroups(cart), locked = !!F.pinnedDatedBooking(cart), pin = F.gid();
    var others = [], mine = null;
    groups.forEach(function (g, i) { var v = groupView(cart, g, i); if (locked && g.gid === pin) mine = v; else others.push(v); });
    locked = locked && !!mine;
    var series = F.baseKey(), offer = !locked && OFFER && others.some(function (o) { return o.series === series; });
    return { others: others, locked: locked, mine: mine, offer: offer ? OFFER : null, orderDiscount: cartLevelDiscount(cart), orderDiscountName: cartLevelName(cart) };
  }

  /* ---------- markup ---------- */
  var ICON = {
    plus: '<svg viewBox="0 0 24 24" fill="none" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>',
    x: '<svg viewBox="0 0 24 24" fill="none" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M7 7l10 10M17 7L7 17"/></svg>',
    lock: '<svg viewBox="0 0 24 24" fill="none" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
    chev: '<svg class="ec-chev" viewBox="0 0 24 24" fill="none" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>',
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    tplus: '<svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>',
    check: '<svg class="a-check" viewBox="0 0 24 24" fill="none" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>'
  };
  function crow(i) {
    var ctl = i.kind === 'base' ? '' // no base swap here (Gabe, 2026-09-25)
      : '<button type="button" class="ec-x" data-ec-rm="' + i.kind + ':' + i.id + '" aria-label="Remove ' + esc(i.name) + '">' + ICON.x + '</button>';
    return '<div class="ec-row" data-ec-item="' + i.kind + ':' + i.id + '"><div class="ec-cr"><span class="ec-cn">' + nb(i.name) + '</span><span class="ec-cp">' + money(i.price) + '</span>' + ctl + '</div></div>';
  }
  function rorow(r) { return '<div class="ec-row"><div class="ec-cr ro"><span class="ec-cn">' + nb(r.name) + '</span><span class="ec-cp">' + money(r.price) + '</span></div></div>'; }
  function editRows() {
    var l = items(), h = '';
    l.forEach(function (i) { h += crow(i); });
    coveredPicked().forEach(function (cv) { h += '<p class="ec-note ec-cnote" data-ec-covered="' + cv.id + '">' + esc(cv.name) + ' is part of your ' + esc(cv.by) + ' focus area, so it is not charged twice.</p>'; });
    if (onWb() && l.some(function (i) { return i.kind === 'up'; })) h += '<p class="ec-note ec-cnote">Changing the focus area reloads the page.</p>';
    h += '<div class="ec-cadd"><button type="button" class="ec-mini" data-ec-add aria-haspopup="dialog">' + ICON.plus + 'Add a test</button>' +
      (focusOptions().length ? '<button type="button" class="ec-mini" data-ec-addfocus aria-haspopup="dialog">' + ICON.plus + 'Add a focus area</button>' : '') + '</div>';
    return '<div class="ec-crows">' + h + '</div>';
  }
  function heldErr() { return HELD_ERR ? '<p class="ec-note ec-herr" role="alert" data-ec-herr>' + esc(HELD_ERR) + '</p>' : ''; }
  function heldAdd(v) { return v.editable ? '<div class="ec-cadd"><button type="button" class="ec-mini" data-ec-hadd="' + esc(v.gid) + '" aria-haspopup="dialog">' + ICON.plus + 'Add a test</button></div>' : ''; }
  function heldRow(v, r) {
    if (!r.rm) return rorow(r);
    return '<div class="ec-row" data-ec-hrow="' + esc(r.rm) + '"><div class="ec-cr ro ec-crm"><span class="ec-cn">' + nb(r.name) + '</span><span class="ec-cp">' + money(r.price) + '</span>' +
      '<button type="button" class="ec-x" data-ec-hrm="' + esc(v.gid) + '|' + esc(r.rm) + '" aria-label="Remove ' + esc(r.name) + '">' + ICON.x + '</button></div></div>';
  }
  function heldLine(v, asToggle) {
    if (asToggle) return '<button type="button" class="ec-cheld ec-htog" data-ec-htog="' + esc(v.gid) + '" aria-expanded="' + !!HOPEN[v.gid] + '" data-ec-held="' + esc(v.gid) + '">' + heldInner(v) + '<span class="ec-mini ec-act">Edit' + ICON.chev + '</span></button>';
    return '<div class="ec-cheld" data-ec-held="' + esc(v.gid) + '">' + heldInner(v) + '</div>';
  }
  function heldBlock(v, phone) { // an earlier booked checkup: its line, then its rows (phones: behind its own toggle)
    var body = '<div class="ec-hbody" data-ec-hbody="' + esc(v.gid) + '"' + (phone && !HOPEN[v.gid] ? ' hidden' : '') + '>' + lockedRows(v) + heldAdd(v) + '</div>';
    // the discount is shown once: as the row under the items when the rows are on the card, else on the line
    return '<div class="ec-hg">' + heldLine(v.editable ? v : Object.assign({}, v, { noRows: true }), phone && v.editable) + (v.editable ? body : '') + '</div>';
  }
  function heldInner(v) {
    return '<span class="ec-tt"><b>' + esc(v.label) + '</b><span>' + ICON.lock + 'Time held' + (v.date ? ' · ' + esc(v.date) : '') + '</span>' + (v.discount && v.noRows ? '<span class="ec-hdisc" data-ec-hdisc>' + esc(v.discountName) + ' <em data-ec-hdisc-amt>' + money(-v.discount) + '</em></span>' : '') + '</span><span class="ec-cp">' + money(v.total) + '</span>';
  }
  function grandTotal(m) { var t = m.locked ? m.mine.total : F.total(); m.others.forEach(function (o) { t += o.total; }); return t - m.orderDiscount; }
  function grandLabel(m) { var n = m.others.length + 1; return m.offer ? 'Total before booking' : 'Total for ' + n + ' checkups'; }
  function offerNote(m) { return m.offer ? '<p class="ec-note ec-offer" data-ec-offer>' + esc(m.offer.head) + '. ' + esc(m.offer.sub) + '</p>' : ''; }
  function grandBlock(m) {
    if (!m.others.length) return '';
    return (m.orderDiscount ? '<div class="ec-row"><div class="ec-cr ro"><span class="ec-cn">' + esc(m.orderDiscountName) + '</span><span class="ec-cp">' + money(-m.orderDiscount) + '</span></div></div>' : '') +
      '<div class="ec-cgrand"><span data-ec-grand-label>' + esc(grandLabel(m)) + '</span><b data-ec-grand>' + money(grandTotal(m)) + '</b></div>' + offerNote(m);
  }
  function lockedRows(v) {
    return '<div class="ec-crows" data-ec-rows="' + esc(v.gid) + '">' + v.rows.map(function (r) { return heldRow(v, r); }).join('') +
      (v.discount ? '<div class="ec-row"><div class="ec-cr ro" data-ec-discount><span class="ec-cn">' + esc(v.discountName) + '</span><span class="ec-cp">' + money(-v.discount) + '</span></div></div>' : '') + '</div>';
  }
  function curLabel(m) { return m.locked ? m.mine.label : (m.others.length ? 'Checkup ' + (m.others.length + 1) : 'Your checkup'); }
  function curTotal(m) { return m.locked ? m.mine.total : F.total(); }

  function collapsedHTML(m) {
    var sub = m.locked ? ICON.lock + 'Time held' + (m.mine.date ? ' · ' + esc(m.mine.date) : '') : esc(summary());
    var h = '<div class="ec-ck ec-fold' + (OPEN ? ' open' : '') + '">' + m.others.map(function (v) { return heldBlock(v, true); }).join('');
    h += '<button type="button" class="ec-tog" data-ec-tog aria-expanded="' + OPEN + '" aria-controls="ecFoldBody"><span class="ec-tt"><b>' + esc(curLabel(m)) + '</b><span data-ec-sum>' + sub + '</span></span>' +
      '<span class="ec-tp" data-ec-total>' + money(curTotal(m)) + '</span><span class="ec-mini ec-act">' + (m.locked && !m.mine.editable ? 'View' : 'Edit') + ICON.chev + '</span></button>';
    var body = (m.locked ? lockedRows(m.mine) + heldAdd(m.mine) : editRows()) + heldErr() + (m.others.length ? grandBlock(m) : offerNote(m));
    h += '<div class="ec-fbody" id="ecFoldBody"' + (OPEN ? '' : ' inert') + '><div>' + body + '</div></div></div>';
    return h;
  }
  function condensedHTML(m) {
    var h = '<div class="ec-ck ec-c">';
    if (m.others.length) {
      h += '<div class="ec-chead"><b>Your checkups</b><span data-ec-grand-top>' + money(grandTotal(m)) + '</span></div>' + m.others.map(function (v) { return heldBlock(v, false); }).join('');
      h += m.locked ? heldLine(m.mine) : '<div class="ec-csub"><b>' + esc(curLabel(m)) + '</b> · not booked yet<span data-ec-total>' + money(curTotal(m)) + '</span></div>';
    } else {
      h += '<div class="ec-chead"><b>' + (m.locked ? ICON.lock + 'Time held' + (m.mine.date ? ' · ' + esc(m.mine.date) : '') : 'Your checkup') + '</b><span data-ec-total>' + money(curTotal(m)) + '</span></div>';
    }
    h += m.locked ? lockedRows(m.mine) + heldAdd(m.mine) : editRows();
    return h + heldErr() + grandBlock(m) + '</div>';
  }
  // "Tuesday, October 6 · 7:00 AM – 12:00 PM, Seoul time" from the held line's own booking fields.
  function heldWhen(l) {
    var p = (l && l.properties) || {};
    var day = String(p['_Start Date'] || '').split(',')[0], date = String(p.Date || '').replace(/,\s*\d{4}\s*$/, '');
    if (!day || !date) return '';
    var end = String(p['_End Time'] || '').toUpperCase();
    return day + ', ' + date + (p.Time ? ' · ' + p.Time + (end ? ' – ' + end : '') : '') + (p['_Booking Timezone'] === 'Asia/Seoul' ? ', Seoul time' : '');
  }
  function render() {
    var m = model(), L = desk.matches ? 'condensed' : 'collapsed';
    card.setAttribute('data-layout', L);
    card.setAttribute('data-mode', m.locked ? 'locked' : (m.others.length ? 'multi' : 'edit'));
    card.innerHTML = L === 'condensed' ? condensedHTML(m) : collapsedHTML(m);
    card.hidden = false;
    /* 2026-10-02 (Gabe: no flashing). This render owns the booked state, decided from the cart in one
       step: body.cf-booked (CSS hides the booking app's slot), the "Your date" heading and the held date
       under it. body.cf-cart-read lifts the CSS that keeps the booking card invisible until the first
       cart read, so a returning visitor never sees the calendar paint and then vanish. */
    if (CART) document.body.classList.add('cf-cart-read');
    document.body.classList.toggle('cf-booked', !!m.locked);
    if (bookingH3) bookingH3.textContent = m.locked ? 'Your date' : (m.others.length ? 'Choose a date for ' + curLabel(m) : bookingH3Orig);
    if (bookingSub) {
      var when = m.locked ? heldWhen(F.pinnedDatedBooking(CART || { items: [] })) : '';
      if (bookingSub.textContent !== (when || bookingSubOrig)) bookingSub.textContent = when || bookingSubOrig;
      bookingSub.classList.toggle('cf-held-when', !!when);
    }
  }

  /* ---------- the Whole Body MRI focus change (save + reload) ---------- */
  function saveAndReload() {
    document.documentElement.classList.add('ec-reloading');
    window.location.reload();
  }

  /* ---------- the sheet ---------- */
  var sheet, backdrop, lastFocus = null;
  function buildSheet() {
    backdrop = document.createElement('div'); backdrop.className = 'inc-backdrop ec-backdrop';
    sheet = document.createElement('section'); sheet.className = 'inc-sheet ec-sheet'; sheet.id = 'ecSheet';
    sheet.setAttribute('role', 'dialog'); sheet.setAttribute('aria-modal', 'true'); sheet.setAttribute('aria-labelledby', 'ecSheetH');
    document.body.appendChild(backdrop); document.body.appendChild(sheet);
    backdrop.addEventListener('click', function () { closeSheet(false); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && sheetKind) closeSheet(false); });
  }
  function sheetTotal() { var t = sheet.querySelector('[data-ec-sheet-total]'); if (t) t.textContent = money(sheetKind === 'focus' && onWb() ? draftTotal() : F.total()); }
  function draftTotal() { // WB only: what the total becomes if the drafted focus change is saved
    var c = C(), t = F.total(), s = S();
    Object.keys(focusDraft || {}).forEach(function (k) { if (!!focusDraft[k] !== !!s.upgrades[k]) t += (focusDraft[k] ? 1 : -1) * c.UPGRADES[k].price; });
    return t;
  }
  function focusRow(k) {
    var u = C().UPGRADES[k];
    return '<div class="addon" data-ec-up="' + k + '" tabindex="0"><div class="an"><b>' + esc(upName(k)) + '</b><span>' + esc(u.tests.join(' · ')) + '</span></div>' +
      '<span class="ap">' + money(u.price) + '</span><button class="atoggle" type="button" aria-pressed="false" aria-label="Add ' + esc(upName(k)) + '">' + ICON.tplus + ICON.check + '</button></div>';
  }
  function paintFocusRow(r) {
    var k = r.getAttribute('data-ec-up'), on = onWb() ? !!focusDraft[k] : !!S().upgrades[k];
    r.classList.toggle('added', on); r.querySelector('.atoggle').setAttribute('aria-pressed', on ? 'true' : 'false');
    var span = r.querySelector('.an span'), c = C(), picked = [];
    if (!span.hasAttribute('data-orig')) span.setAttribute('data-orig', span.textContent);
    Object.keys(c.ADDONS).forEach(function (a) { if (c.ADDONS[a].includedIn === k && S().addons[a]) picked.push(c.ADDONS[a].name); });
    span.textContent = on && picked.length ? 'Includes your ' + picked.join(' and ') + ', so it is not charged twice.' : span.getAttribute('data-orig');
  }
  function toggleFocus(r) {
    var k = r.getAttribute('data-ec-up');
    if (onWb()) focusDraft[k] = !focusDraft[k];
    else edit(function (s) { s.upgrades[k] = !s.upgrades[k]; }); // the builder's own toggle: upgrades[id] = added, saved
    paintFocusRow(r); sheetFoot(); sheetTotal();
  }
  function sheetFoot() {
    var d = sheet.querySelector('[data-ec-done]'); if (!d) return;
    var changed = sheetKind === 'focus' && onWb() && Object.keys(focusDraft).some(function (k) { return !!focusDraft[k] !== !!S().upgrades[k]; });
    d.textContent = changed ? 'Save and reload' : 'Done';
  }
  function openSheet(kind, from) {
    sheetKind = kind; sheetFrom = from || null; lastFocus = document.activeElement;
    var who = model().others.length ? ' to ' + curLabel(model()) : '';
    var title = kind === 'addon' ? 'Add a test' + who : 'Add a focus area' + who;
    var hint = kind === 'addon' ? 'Tap a row to add it. Tap again to take it off.' : (onWb() ? 'Adding it reloads the page so the calendar matches.' : 'Tap a row to add it. Tap again to take it off.');
    var body = '';
    if (kind === 'addon') {
      var tpl = document.getElementById('ecAddonGroups');
      body = tpl ? tpl.innerHTML : '';
    } else {
      focusDraft = {}; var s = S(); Object.keys(C().UPGRADES).forEach(function (k) { focusDraft[k] = !!s.upgrades[k]; });
      body = '<div class="addon-group">' + focusOptions().map(focusRow).join('') + '</div>';
    }
    sheet.innerHTML = '<div class="inc-handle"></div><div class="inc-head"><div><h3 id="ecSheetH">' + esc(title) + '</h3><p>' + esc(hint) + '</p></div>' +
      '<button type="button" class="inc-close" aria-label="Close">' + ICON.close + '</button></div><div class="inc-body">' + body + '</div>' +
      '<div class="inc-foot"><div class="inc-price">' + esc(curLabel(model())) + '<b data-ec-sheet-total>' + money(F.total()) + '</b></div><button type="button" class="ec-pill solid" data-ec-done>Done</button></div>';
    if (kind === 'addon') {
      // the add-on page's own wiring: drops the other base's rows, locks covered ones, whole-row tap
      F.wireAddons();
      sheet.querySelector('.inc-body').addEventListener('click', function () { setTimeout(sheetTotal, 0); });
      sheet.querySelector('.inc-body').addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') setTimeout(sheetTotal, 0); });
    } else {
      Array.prototype.forEach.call(sheet.querySelectorAll('[data-ec-up]'), function (r) {
        var t = r.querySelector('.atoggle');
        paintFocusRow(r);
        t.addEventListener('click', function () { toggleFocus(r); });
        r.addEventListener('click', function (e) { if (e.target.closest('button, a')) return; toggleFocus(r); });
        r.addEventListener('keydown', function (e) { if (e.target !== r) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFocus(r); } });
      });
    }
    sheet.querySelector('.inc-close').addEventListener('click', function () { closeSheet(false); });
    sheet.querySelector('[data-ec-done]').addEventListener('click', function () { closeSheet(true); });
    sheetTotal(); sheetFoot();
    document.body.classList.add('inc-lock');
    requestAnimationFrame(function () { sheet.setAttribute('data-open', '1'); backdrop.setAttribute('data-open', '1'); });
    sheet.querySelector('.inc-close').focus({ preventScroll: true });
  }
  function closeSheet(done) {
    if (!sheetKind) return;
    var kind = sheetKind; sheetKind = null;
    sheet.removeAttribute('data-open'); backdrop.removeAttribute('data-open'); document.body.classList.remove('inc-lock');
    if (kind === 'held') {
      var gid = HSHEET; HSHEET = null; render();
      var hb = card.querySelector('[data-ec-hadd="' + gid + '"]'); if (hb) hb.focus({ preventScroll: true });
      return;
    }
    if (kind === 'focus' && onWb() && done) {
      var s = S(), changed = Object.keys(focusDraft).some(function (k) { return !!focusDraft[k] !== !!s.upgrades[k]; });
      if (changed) { edit(function (st) { Object.keys(focusDraft).forEach(function (k) { st.upgrades[k] = !!focusDraft[k]; }); }); saveAndReload(); return; }
    }
    render();
    var back = card.querySelector(kind === 'addon' ? '[data-ec-add]' : '[data-ec-addfocus]') || card.querySelector('[data-ec-add]');
    if (back) back.focus({ preventScroll: true }); else if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }

  /* ---------- clicks in the card ---------- */
  function locked() { return model().locked; }
  function onClick(e) {
    var tog = e.target.closest('[data-ec-tog]');
    if (tog) {
      OPEN = !OPEN;
      var f = card.querySelector('.ec-fold'), b = card.querySelector('.ec-fbody');
      f.classList.toggle('open', OPEN); tog.setAttribute('aria-expanded', String(OPEN));
      if (OPEN) b.removeAttribute('inert'); else b.setAttribute('inert', '');
      return;
    }
    var ht = e.target.closest('[data-ec-htog]');
    if (ht) {
      var hg = ht.getAttribute('data-ec-htog'); HOPEN[hg] = !HOPEN[hg];
      ht.setAttribute('aria-expanded', String(!!HOPEN[hg]));
      var hbody = card.querySelector('[data-ec-hbody="' + hg + '"]'); if (hbody) hbody.hidden = !HOPEN[hg];
      return;
    }
    var hr = e.target.closest('[data-ec-hrm]');
    if (hr) {
      if (HELD_BUSY) return;
      var q = hr.getAttribute('data-ec-hrm').split('|'), what = q[1].split(':'), hrow = hr.closest('.ec-row');
      if (hrow) hrow.classList.add('out');
      if (what[0] === 'addon') removeAddon(q[0], what[1]); else removeFocus(q[0], what[1]);
      return;
    }
    var ha = e.target.closest('[data-ec-hadd]');
    if (ha) { if (!HELD_BUSY) openHeldSheet(ha.getAttribute('data-ec-hadd')); return; }
    if (locked()) return; // nothing below may run once a time is held (the rows are read-only then anyway)
    var rm = e.target.closest('[data-ec-rm]');
    if (rm) {
      var row = rm.closest('.ec-row');
      if (row.classList.contains('out')) return; // a second tap on a row already leaving
      var p = rm.getAttribute('data-ec-rm').split(':'), wbFocus = p[0] === 'up' && onWb();
      edit(function (s) { if (p[0] === 'up') s.upgrades[p[1]] = false; else s.addons[p[1]] = false; }); // renderSummary's own remove
      row.classList.add('out');
      setTimeout(wbFocus ? saveAndReload : render, 260);
      return;
    }
    if (e.target.closest('[data-ec-add]')) { openSheet('addon'); return; }
    if (e.target.closest('[data-ec-addfocus]')) { openSheet('focus'); return; }
  }
  /* ---------- editing a booked checkup (see the header note) ---------- */
  function sig(l) { return JSON.stringify([Number(l.variant_id), l.quantity, l.original_line_price, l.properties || {}]); }
  function canon(o) { var k = Object.keys(o || {}).sort(), r = {}; k.forEach(function (x) { r[x] = o[x]; }); return JSON.stringify(r); }
  function post(url, body) {
    var init = { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) };
    init.__himediInternal = true; // the flow's own marker: its /cart/add rewrite and group stamping leave this request alone
    return fetch(url, init).then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); });
  }
  // after == before, except: `gone` keys removed, and exactly the `added` [{variant, props}] lines new
  function cartIs(before, after, gone, added) {
    var want = {}, n = 0, ok = true, fresh = [];
    (before.items || []).forEach(function (l) { if (gone.indexOf(l.key) === -1) { want[sig(l)] = (want[sig(l)] || 0) + 1; } });
    var beforeKeys = {}; (before.items || []).forEach(function (l) { beforeKeys[l.key] = 1; });
    (after.items || []).forEach(function (l) {
      if (gone.indexOf(l.key) !== -1) { ok = false; return; }
      var s2 = sig(l); if (want[s2]) { want[s2]--; return; }
      fresh.push(l);
    });
    Object.keys(want).forEach(function (s2) { if (want[s2]) ok = false; });
    if (fresh.length !== added.length) ok = false;
    added.forEach(function (a) {
      var i, hit = -1;
      for (i = 0; i < fresh.length; i++) if (Number(fresh[i].variant_id) === Number(a.variant) && (fresh[i].quantity || 1) === 1 && canon(fresh[i].properties) === canon(a.props)) { hit = i; break; }
      if (hit === -1) ok = false; else { a.line = fresh[hit]; fresh.splice(hit, 1); }
    });
    void n;
    return ok;
  }
  // put the cart back to `before`: drop every line it did not have, re-add every line it had and lost
  function restore(before) {
    return readCart().then(function (now) {
      var had = {}, drop = {}, readd = [], seen = {};
      (before.items || []).forEach(function (l) { had[sig(l)] = (had[sig(l)] || 0) + 1; });
      (now.items || []).forEach(function (l) { var s2 = sig(l); if (had[s2]) { had[s2]--; seen[l.key] = 1; } else drop[l.key] = 0; });
      (before.items || []).forEach(function (l) {
        var s2 = sig(l); if (had[s2] > 0) { had[s2]--; readd.push({ id: Number(l.variant_id), quantity: l.quantity || 1, properties: l.properties || {} }); }
      });
      var p = Promise.resolve();
      if (readd.length) p = p.then(function () { return post('/cart/add.js', { items: readd }); });
      if (Object.keys(drop).length) p = p.then(function () { return post('/cart/update.js', { updates: drop }); });
      return p.then(readCart).then(function (after) { return cartIs(before, after, [], []); });
    });
  }
  // does the saved build describe this group (so it must move with the edit)?
  function describes(cart, gid) {
    if (gid !== F.gid()) return false;
    var have = []; (cart.items || []).forEach(function (l) { if ((l.properties || {})[F.GKEY] === gid) for (var i = 0; i < (l.quantity || 1); i++) have.push(Number(l.variant_id)); });
    var d = F.diff(have, F.wantFor(have));
    return !d.missing.length && !d.extra.length;
  }
  function syncBuild(fn) {
    var sealedMatch = F.pinSealed() && S().committedBuild === F.buildHash();
    edit(fn);
    if (sealedMatch) edit(function (s) { s.committedBuild = F.buildHash(); });
  }
  function groupTags(cart, gid) { // exactly what the flow's reconcile puts on a line it adds to a group
    var f = F.groupFields(cart, gid), props = {};
    props[F.GLABEL] = f.label; props[F.GKEY] = gid; props[F.VKEY] = F.CART_SCHEMA;
    if (f.seq) props[F.SKEY] = f.seq;
    return props;
  }
  var ERR = 'That did not go through. Your checkup and time are as they were.';
  function heldOp(fn) {
    if (HELD_BUSY) return Promise.resolve();
    HELD_BUSY = true; HELD_ERR = null; card.setAttribute('data-busy', '1'); if (sheet) sheet.setAttribute('data-busy', '1');
    return readCart().then(fn).catch(function (e) {
      try { console.error('[himedi summary edit]', e); } catch (_) {}
      HELD_ERR = (e && e.shown) || ERR;
    }).then(function () {
      HELD_BUSY = false; card.removeAttribute('data-busy'); if (sheet) sheet.removeAttribute('data-busy');
      if (window.HimediNextStep && typeof window.HimediNextStep.refresh === 'function') { try { window.HimediNextStep.refresh(); } catch (_) {} } // header badge + pinned "Checkout · $total"
      return readCart().then(function () { render(); if (sheetKind === 'held') paintHeldSheet(); }, render);
    });
  }
  function shown(msg) { var e = new Error(msg); e.shown = msg; return e; }
  function infoFor(cart, gid) { var g = findGroup(cart, gid); var i = g ? bookedInfo(cart, g) : null; if (!i || !i.ok) throw new Error('group ' + gid + ' is not an editable booked checkup'); return i; }

  /* The three booked-checkup writes, UI-free. Each takes the cart it just read, and either resolves with the
     cart as intended or puts the cart back and rejects. The summary card runs them inside heldOp(); the cart page
     (himedi-cart-edit.js) runs the same functions through window.HimediBookedEdit. One implementation. */
  function removeAddon(gid, k) { return heldOp(function (before) { return opRemoveAddon(before, gid, k); }); }
  function addAddon(gid, k) { return heldOp(function (before) { return opAddAddon(before, gid, k); }); }
  function removeFocus(gid, f) { return heldOp(function (before) { return opRemoveFocus(before, gid, f); }); }
  function opRemoveAddon(before, gid, k) {
    {
      var info = infoFor(before, gid), line = info.addons[k];
      if (!line) throw new Error('no ' + k + ' line in ' + gid);
      var sync = describes(before, gid), was = S().addons[k];
      if (sync) syncBuild(function (s) { s.addons[k] = false; }); // before the write: the reconcile only adds
      var upd = {}; upd[line.key] = 0;
      return post('/cart/update.js', { updates: upd }).then(readCart).then(function (after) {
        if (!cartIs(before, after, [line.key], [])) throw new Error('cart changed beyond the one line');
      }).catch(function (e) {
        if (sync) syncBuild(function (s) { s.addons[k] = was; });
        return restore(before).then(function () { throw e; });
      });
    }
  }
  function opAddAddon(before, gid, k) {
    {
      var info = infoFor(before, gid), a = (C().ADDONS_BY_BASE[info.base] || {})[k];
      if (!a || info.addons[k] || coveredIn(info, k)) throw new Error(k + ' cannot be added to ' + gid);
      var sync = describes(before, gid), props = groupTags(before, gid), want = [{ variant: a.variant, props: props }];
      return post('/cart/add.js', { items: [{ id: Number(a.variant), quantity: 1, properties: props }] }).then(readCart).then(function (after) {
        if (!cartIs(before, after, [], want)) throw new Error('cart changed beyond the one new line');
        if (sync) syncBuild(function (s) { s.addons[k] = true; }); // after the write, so a reconcile cannot add it a second time
      }).catch(function (e) { return restore(before).then(function () { throw e; }); });
    }
  }
  /* Taking a focus area off: the dated line's variant changes, so it is a swap. New line first, checked,
     then the old one goes; the cart is never without its dated line, and never keeps both. */
  function opRemoveFocus(before, gid, f) {
    {
      var c = C(), info = infoFor(before, gid), dl = info.dated, newV;
      if (info.foci.indexOf(f) === -1) throw new Error(f + ' is not on ' + gid);
      if (info.base === 'baseline') {
        var rest = info.foci.filter(function (x) { return x !== f; }).sort(), e = c.COMBO_VARIANTS[rest.join('+') || 'base'];
        newV = e && e[info.twin]; // the same Standard/Promo twin the booking app's add already got
      } else { // MRI + Baseline -> MRI only (one booking event holds both); a Promo-twin booking stays on its Promo twin
        newV = c.BASES.wbmri.variant;
        if (info.twin === 'promo' && c.WBMRI_STD_TO_PROMO && c.WBMRI_STD_TO_PROMO[String(newV)]) newV = c.WBMRI_STD_TO_PROMO[String(newV)];
      }
      if (!newV) throw new Error('no variant for ' + gid + ' without ' + f);
      var sync = describes(before, gid);
      var back = []; // chosen add-ons the focus area was covering (the builder's rule): only if this build chose them
      if (sync) Object.keys(c.ADDONS).forEach(function (k) { if (S().addons[k] && c.ADDONS[k].includedIn === f && c.ADDONS[k].variant && !info.addons[k]) back.push(k); });
      var props = JSON.parse(JSON.stringify(dl.properties)), step1 = [{ variant: newV, props: props }];
      return post('/cart/add.js', { items: [{ id: Number(newV), quantity: 1, properties: props }] }).then(readCart).then(function (mid) {
        if (!cartIs(before, mid, [], step1)) throw new Error('new dated line did not arrive exactly');
        var upd = {}; upd[dl.key] = 0;
        return post('/cart/update.js', { updates: upd }).then(readCart).then(function (after) {
          if (!cartIs(before, after, [dl.key], [{ variant: newV, props: props }])) throw new Error('swap did not leave exactly one new dated line');
          if (!back.length) return after;
          var tags = groupTags(after, gid), items = back.map(function (k) { return { id: Number(c.ADDONS[k].variant), quantity: 1, properties: tags }; });
          return post('/cart/add.js', { items: items }).then(readCart).then(function (a2) {
            if (!cartIs(after, a2, [], back.map(function (k) { return { variant: c.ADDONS[k].variant, props: tags }; }))) throw new Error('add-back changed more than expected');
            return a2;
          });
        });
      }).then(function () {
        if (sync) syncBuild(function (s) { s.upgrades[f] = false; });
      }).catch(function (e) { return restore(before).then(function () { throw e; }); });
    }
  }

  /* the add-on sheet for a booked checkup: the add-on page's rows for its base, its own cover rule */
  function paintHeldSheet() {
    if (!sheet || sheetKind !== 'held') return;
    var cart = CART || { items: [] }, g = findGroup(cart, HSHEET), info = g ? bookedInfo(cart, g) : null, c = C();
    if (!info || !info.ok) { closeSheet(false); return; }
    Array.prototype.forEach.call(sheet.querySelectorAll('.addon[data-id]'), function (row) {
      var id = row.getAttribute('data-id'), a = c.ADDONS_BY_BASE[info.base][id], t = row.querySelector('.atoggle'), note = row.querySelector('.an span');
      var covered = coveredIn(info, id), on = !covered && !!info.addons[id];
      row.classList.toggle('added', on); row.classList.toggle('covered', covered);
      if (note) {
        if (!note.hasAttribute('data-orig')) note.setAttribute('data-orig', note.textContent);
        note.textContent = covered ? a.cat + ' · already included in your ' + c.UPGRADES[a.includedIn].name + ' focus area' : note.getAttribute('data-orig');
      }
      if (t) { t.disabled = covered; t.setAttribute('aria-disabled', covered ? 'true' : 'false'); t.setAttribute('aria-pressed', on ? 'true' : 'false'); }
      row.setAttribute('tabindex', covered ? '-1' : '0');
    });
    var v = groupView(cart, g, 0), tt = sheet.querySelector('[data-ec-sheet-total]');
    if (tt) tt.textContent = money(v.total);
  }
  function openHeldSheet(gid) {
    var cart = CART || { items: [] }, g = findGroup(cart, gid), info = g ? bookedInfo(cart, g) : null;
    if (!info || !info.ok) return;
    sheetKind = 'held'; HSHEET = gid; lastFocus = document.activeElement;
    var label = ((g.lines[0] || {}).properties || {}).Checkup || 'your checkup';
    var tpl = document.getElementById('ecAddonGroups');
    sheet.innerHTML = '<div class="inc-handle"></div><div class="inc-head"><div><h3 id="ecSheetH">' + esc('Add a test to ' + label) + '</h3><p>Tap a row to add it. Tap again to take it off. Your time stays the same.</p></div>' +
      '<button type="button" class="inc-close" aria-label="Close">' + ICON.close + '</button></div><div class="inc-body">' + (tpl ? tpl.innerHTML : '') + '</div>' +
      '<div class="inc-foot"><div class="inc-price">' + esc(label) + '<b data-ec-sheet-total></b></div><button type="button" class="ec-pill solid" data-ec-done>Done</button></div>';
    Array.prototype.forEach.call(sheet.querySelectorAll('.addon[data-base]'), function (row) { if (row.getAttribute('data-base') !== info.base) row.parentNode.removeChild(row); });
    Array.prototype.forEach.call(sheet.querySelectorAll('.addon-group'), function (grp) { if (!grp.querySelector('.addon')) grp.hidden = true; });
    Array.prototype.forEach.call(sheet.querySelectorAll('.addon[data-id] .atoggle'), function (t) { if (!t.querySelector('.a-check')) t.insertAdjacentHTML('beforeend', ICON.check); });
    var tap = function (row) {
      if (HELD_BUSY || row.classList.contains('covered')) return;
      var id = row.getAttribute('data-id'), cur = findGroup(CART || { items: [] }, gid), inf = cur ? bookedInfo(CART, cur) : null;
      if (!inf || !inf.ok) return;
      if (inf.addons[id]) removeAddon(gid, id); else addAddon(gid, id);
    };
    sheet.querySelector('.inc-body').addEventListener('click', function (e) {
      var row = e.target.closest('.addon[data-id]'); if (!row) return;
      if (e.target.closest('a, input, select, textarea')) return;
      tap(row);
    });
    sheet.querySelector('.inc-body').addEventListener('keydown', function (e) {
      var row = e.target.closest && e.target.closest('.addon[data-id]'); if (!row || e.target !== row) return;
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tap(row); }
    });
    sheet.querySelector('.inc-close').addEventListener('click', function () { closeSheet(false); });
    sheet.querySelector('[data-ec-done]').addEventListener('click', function () { closeSheet(true); });
    paintHeldSheet();
    document.body.classList.add('inc-lock');
    requestAnimationFrame(function () { sheet.setAttribute('data-open', '1'); backdrop.setAttribute('data-open', '1'); });
    sheet.querySelector('.inc-close').focus({ preventScroll: true });
  }

  /* ---------- mount ---------- */
  function refreshFromCart() { return readCart().then(render, render); }
  function mount() {
    var booking = document.querySelector('.booking');
    bookingSec = booking && booking.closest('section');
    if (!bookingSec || !bookingSec.parentNode) return;
    bookingSec.parentNode.classList.add('ec-wide');
    bookingSec.classList.add('ec-booksec');
    bookingH3 = booking.querySelector('h3'); bookingH3Orig = bookingH3 ? bookingH3.textContent : '';
    bookingSub = booking.querySelector(':scope > p.sub'); bookingSubOrig = bookingSub ? bookingSub.textContent : '';
    card = document.createElement('section'); card.className = 'ec'; card.id = 'ecCard'; card.hidden = true;
    card.setAttribute('aria-label', 'Your checkup');
    bookingSec.parentNode.insertBefore(card, bookingSec);
    card.addEventListener('click', onClick);
    if (desk.addEventListener) desk.addEventListener('change', render); else if (desk.addListener) desk.addListener(render);
    buildSheet();
    render();                 // from state at once; the cart read below only adds earlier checkups / the lock
    cartReady.then(render, render);
    // the booking app fires no event: the flow file un-hides #cfCommit once this build's time is held
    commitBox = document.getElementById('cfCommit');
    if (commitBox && 'MutationObserver' in window) {
      new MutationObserver(function () {
        if (commitBox.hidden) return;
        if (sheetKind) closeSheet(false);
        [0, 1500, 4000, 8000].forEach(function (ms) { setTimeout(refreshFromCart, ms); });
      }).observe(commitBox, { attributes: true, attributeFilter: ['hidden'] });
    }
    /* Back/forward cache: a page restored from it keeps the flow's in-memory build, which the booking
       interceptor reads. If the builder changed the stored build meanwhile, reload so they agree. */
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      var ls = {}; try { ls = JSON.parse(localStorage.getItem('himedi_checkup_v12')) || {}; } catch (_) {}
      var mem = F.state(), pick = function (o) { return JSON.stringify([o.base || 'baseline', o.upgrades || {}, o.addons || {}]); };
      if (pick(ls) !== pick(mem)) { window.location.reload(); return; }
      refreshFromCart();
    });
  }
  /* Shared with the cart page. Reads and writes go through the same functions the card uses. */
  window.HimediBookedEdit = { readCart: readCart, bookedInfo: bookedInfo, lineRows: lineRows, findGroup: findGroup, addonKeyFor: addonKeyFor,
    describes: describes, removeAddon: opRemoveAddon, addAddon: opAddAddon, removeFocus: opRemoveFocus, ERR: ERR, v: 1 };
  if (!document.querySelector('.cf-app-slot')) return; // the card: booking-app mode only

  var cartReady = readCart(); // started at parse time so the card has its final shape before the calendar mounts
  // Mounts right after the flow file has wired the page (2026-10-09, speed): it says so with HimediFlow.ready and the
  // himedi:flow-ready event, which with both files loaded without defer is the moment the page is parsed, before the
  // booking app's deferred script. DOMContentLoaded stays as the fallback for an older flow file without the signal.
  var mounted = false;
  function mountOnce() { if (mounted) return; mounted = true; mount(); }
  if (F.ready) mountOnce();
  else { document.addEventListener('himedi:flow-ready', mountOnce); document.addEventListener('DOMContentLoaded', mountOnce); }

  window.HimediSummaryEdit = { render: render, model: model, items: items, refresh: refreshFromCart, bookedEdit: 1 };
})();
